import * as React from "react";
import { FunctionComponent } from "react";
import {
  Language,
  ColorScheme,
  LanguageMenuItem,
  StoreState,
  PageState,
} from "../types";
import * as ClassNames from "classnames";
import { connect, ConnectedProps, MapStateToProps } from "react-redux";
import { getActiveSite } from "../selectors/sites";
import { getHrefLangAttributes } from "../utils/utils";
import { SiteLanguage } from "../../server/types";
import { style } from "typestyle";
import LanguageMenuSelect from "./LanguageMenuSelect";
import LanguageMenuList from "./LanguageMenuList";

interface Props {
  isPreview: boolean;
  languageId: Language;
  scheme: ColorScheme | undefined;
  pageId: string;
  variant: "horizontal" | "vertical";
  className?: string;
  switchToSelectLimit?: number;
}

interface StateProps {
  items: LanguageMenuItem[];
}

type ReduxProps = ConnectedProps<typeof connector>;

const LanguageMenu: FunctionComponent<Props & ReduxProps> = ({
  isPreview,
  languageId,
  scheme,
  items,
  variant,
  className,
  switchToSelectLimit = 3,
}) => {
  const itemsLength = items.length;
  if (!itemsLength) return null;

  return (
    <div
      className={ClassNames(
        "LanguageMenu",
        `LanguageMenu--${variant}`,
        className,
        style({
          background: scheme?.main.background,
        }),
        {
          "LanguageMenu--show-list-on-wide": itemsLength <= switchToSelectLimit,
        }
      )}
    >
      <LanguageMenuSelect
        isPreview={isPreview}
        items={items}
        languageId={languageId}
      />

      {itemsLength <= switchToSelectLimit && (
        <LanguageMenuList
          isPreview={isPreview}
          items={items}
          languageId={languageId}
        />
      )}
    </div>
  );
};

const getLanguageMenuItems = ({
  siteLanguages,
  pages,
  pageId,
  useFullLanguageNames,
  isPreview,
}: {
  siteLanguages: SiteLanguage[];
  pages: PageState;
  pageId: string;
  useFullLanguageNames: boolean;
  isPreview: boolean;
}): LanguageMenuItem[] => {
  const fullLanguageNames: { [lang in Language]: string } = {
    de: "deutsch",
    it: "italiano",
    en: "english",
    fr: "français",
    da: "dansk",
    es: "español",
    nl: "nederlands",
    pl: "polski",
    ru: "русский",
  };

  return getHrefLangAttributes({
    siteLanguages,
    pages,
    pageId,
    fallbackToHome: true,
    isPreview,
  }).map<LanguageMenuItem>(({ href, hrefLang, rel }) => ({
    children: useFullLanguageNames ? fullLanguageNames[hrefLang] : hrefLang,
    href,
    hrefLang,
    rel,
  }));
};

const mapStateToProps: MapStateToProps<StateProps, Props, StoreState> = (
  { pages, sites },
  { isPreview, pageId, variant }
) => ({
  items: getLanguageMenuItems({
    isPreview,
    pageId,
    pages,
    siteLanguages: getActiveSite(sites).languages,
    useFullLanguageNames: variant === "vertical",
  }),
});

const connector = connect(mapStateToProps);

export default connector(LanguageMenu);
